/**
 * src/lib/debug/consistencyAudit.js
 *
 * Walks every entity in the store and asks each view's contract "would you
 * render this?" Emits cross_view_consistency_check per (view, entityType)
 * pair and returns a JSON-serializable report.
 *
 * Phase Z — definition of "clean": no divergences between what Projects
 * shows and what Gantt hides (and vice-versa) without a logged exclusion.
 *
 * Contract shape (VIEW_CONTRACTS[view].entities[entityType]):
 *   { requiredFields: string[], filter: (entity) => boolean, excludeReasons: string[] }
 */

import { VIEW_CONTRACTS } from '../contracts/renderContract.js';
import { logEvent, getTraceContext } from '../observability.js';

const PHASE_TAG = 'phase_z';

// Singular entity type → store key (plural). The contract owns the inverse
// (PLURAL_TO_SINGULAR) for its own internal use; we own this direction here.
const PLURAL = {
  project: 'projects',
  bundle: 'bundles',
  task: 'tasks',
  deal: 'deals',
  person: 'people',
  vessel_contact: 'vesselContacts',
  purchase_order: 'purchaseOrders',
  supplier: 'suppliers',
  timelog: 'timeLogs',
};

/**
 * wouldRender(spec, entity) — mirrors the logic in applyContract() without
 * the side-effect logging. Returns true if the entity passes all required
 * field checks and the view filter.
 */
function wouldRender(spec, entity) {
  for (const f of spec.requiredFields) {
    if (entity[f] == null || entity[f] === '') return false;
  }
  return !!spec.filter(entity);
}

/**
 * runConsistencyAudit(store, ctx)
 *
 * @param {Object} store  - Flat store keyed by plural entity type
 *                          e.g. { projects: [...], tasks: [...] }
 * @param {Object} ctx    - Call context (userId etc.)
 * @returns {Object}      - JSON-serializable report: { ran_at, phase_tag, views, divergences }
 */
export function runConsistencyAudit(store = {}, ctx = {}) {
  const views = {};
  const divergences = [];

  for (const [viewName, viewSpec] of Object.entries(VIEW_CONTRACTS)) {
    views[viewName] = {};

    for (const [entityType, entitySpec] of Object.entries(viewSpec.entities)) {
      const source = store[PLURAL[entityType]] || [];
      let visible = 0;
      let excluded = 0;

      for (const e of source) {
        if (wouldRender(entitySpec, e)) visible += 1;
        else excluded += 1;
      }

      views[viewName][entityType] = { visible, excluded, total: source.length };

      logEvent('cross_view_consistency_check', `${viewName}:${entityType}`, {
        phase_tag: PHASE_TAG,
        ...getTraceContext(),
        view: viewName,
        entity_type: entityType,
        visible,
        excluded,
        total: source.length,
        user_id: ctx.userId || null,
      });
    }
  }

  // Divergence: an entity type declared in ≥2 views where the visible count
  // differs — indicating the two views would present inconsistent data to the
  // user without an explicit exclusion reason. The per-entity render_exclusion
  // logs (from applyContract) provide the per-row reasons; this audit surfaces
  // the aggregate cross-view count mismatch.
  const entityTypes = new Set();
  for (const vSpec of Object.values(VIEW_CONTRACTS)) {
    for (const eType of Object.keys(vSpec.entities)) entityTypes.add(eType);
  }

  for (const eType of entityTypes) {
    const counts = [];
    for (const [viewName, viewData] of Object.entries(views)) {
      if (viewData[eType] !== undefined) {
        counts.push({ view: viewName, visible: viewData[eType].visible });
      }
    }
    if (counts.length >= 2) {
      const min = Math.min(...counts.map((c) => c.visible));
      const max = Math.max(...counts.map((c) => c.visible));
      if (min !== max) {
        divergences.push({ entity_type: eType, counts });
      }
    }
  }

  return {
    ran_at: new Date().toISOString(),
    phase_tag: PHASE_TAG,
    views,
    divergences,
  };
}
